import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type PointerEvent,
  type WheelEvent,
} from 'react'

type ImageCarouselModalProps = {
  images: string[]
  initialIndex?: number
  onClose: () => void
  imagesObj?: any
}

type Position = {
  x: number
  y: number
}

const MIN_ZOOM = 1
const MAX_ZOOM = 4
const ZOOM_STEP = 0.25

export function ImageCarouselModal({
  images,
  initialIndex = 0,
  onClose,
  imagesObj = null,
}: ImageCarouselModalProps) {
  const [currentIndex, setCurrentIndex] = useState(
    Math.min(Math.max(initialIndex, 0), images.length - 1)
  )

  const [zoom, setZoom] = useState(MIN_ZOOM)

  const [position, setPosition] = useState<Position>({
    x: 0,
    y: 0,
  })

  const [isDragging, setIsDragging] = useState(false)

  const imageContainerRef = useRef<HTMLDivElement>(null)

  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const dragStart = useRef({
    x: 0,
    y: 0,
  })

  const positionStart = useRef<Position>({
    x: 0,
    y: 0,
  })

  const currentImage = images[currentIndex]
  const typeOfFile =
    imagesObj !== null ? imagesObj.find((img) => img.preview === currentImage)?.type : 'image'

  const resetView = useCallback(() => {
    setZoom(MIN_ZOOM)
    setPosition({
      x: 0,
      y: 0,
    })
  }, [])

  const changeImage = useCallback(
    (index: number) => {
      setCurrentIndex(index)
      resetView()
    },
    [resetView]
  )

  const previous = useCallback(() => {
    setCurrentIndex((index) => (index === 0 ? images.length - 1 : index - 1))

    resetView()
  }, [images.length, resetView])

  const next = useCallback(() => {
    setCurrentIndex((index) => (index === images.length - 1 ? 0 : index + 1))

    resetView()
  }, [images.length, resetView])

  /*
   * Molette :
   * - sur le slider => zoom centré
   * - sur l'image => zoom autour du curseur
   */
  const handleWheel = (event: WheelEvent<HTMLElement>) => {
    event.preventDefault()

    const target = event.target as HTMLElement

    const isZoomSlider = target.closest('.image-carousel-modal__zoom') !== null

    const direction = event.deltaY > 0 ? -1 : 1

    if (isZoomSlider) {
      setZoom((currentZoom) => {
        const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, currentZoom + direction * ZOOM_STEP))

        if (nextZoom === MIN_ZOOM) {
          setPosition({
            x: 0,
            y: 0,
          })
        }

        return nextZoom
      })

      return
    }

    const container = imageContainerRef.current

    if (!container) return

    const rect = container.getBoundingClientRect()

    const mouseX = event.clientX - rect.left - rect.width / 2

    const mouseY = event.clientY - rect.top - rect.height / 2

    setZoom((currentZoom) => {
      const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, currentZoom + direction * ZOOM_STEP))

      if (nextZoom === MIN_ZOOM) {
        setPosition({
          x: 0,
          y: 0,
        })

        return MIN_ZOOM
      }

      const ratio = nextZoom / currentZoom

      setPosition((currentPosition) => ({
        x: mouseX - (mouseX - currentPosition.x) * ratio,

        y: mouseY - (mouseY - currentPosition.y) * ratio,
      }))

      return nextZoom
    })
  }

  const handleZoomChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextZoom = Number(event.target.value)

    setZoom(nextZoom)

    if (nextZoom === MIN_ZOOM) {
      setPosition({
        x: 0,
        y: 0,
      })
    }
  }

  const handlePointerDown = (event: PointerEvent<HTMLImageElement>) => {
    if (zoom <= MIN_ZOOM) return

    event.currentTarget.setPointerCapture(event.pointerId)

    setIsDragging(true)

    dragStart.current = {
      x: event.clientX,
      y: event.clientY,
    }

    positionStart.current = {
      ...position,
    }
  }

  const handlePointerMove = (event: PointerEvent<HTMLImageElement>) => {
    if (!isDragging || zoom <= MIN_ZOOM) {
      return
    }

    setPosition({
      x: positionStart.current.x + event.clientX - dragStart.current.x,

      y: positionStart.current.y + event.clientY - dragStart.current.y,
    })
  }

  const handlePointerUp = (event: PointerEvent<HTMLImageElement>) => {
    setIsDragging(false)

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose()
          break

        case 'ArrowLeft':
          if (images.length > 1) {
            previous()
          }
          break

        case 'ArrowRight':
          if (images.length > 1) {
            next()
          }
          break

        case '0':
          resetView()
          break

        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    const previousOverflow = document.body.style.overflow

    document.body.style.overflow = 'hidden'

    closeButtonRef.current?.focus()

    return () => {
      document.removeEventListener('keydown', handleKeyDown)

      document.body.style.overflow = previousOverflow
    }
  }, [images.length, next, onClose, previous, resetView])

  if (!images.length || !currentImage) {
    return null
  }

  return (
    <>
      <style>{`
        .image-carousel-backdrop {
          position: fixed;
          inset: 0;
          z-index: 9999;

          display: flex;
          align-items: center;
          justify-content: center;

          padding: 5vh 5vw;

          background: rgba(0, 0, 0, 0.72);
        }

        .image-carousel-modal {
          position: relative;

          width: 90vw;
          height: 90vh;

          max-width: 1600px;
          max-height: 1000px;

          overflow: hidden;

          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 18px;

          background: #0b0b0d;
          color: #fff;

          box-shadow:
            0 30px 80px rgba(0, 0, 0, 0.5);
        }

        .image-carousel-modal__content {
          position: absolute;
          inset: 0;

          display: flex;
          align-items: center;
          justify-content: center;

          overflow: hidden;

          background: #0b0b0d;
        }

        .image-carousel-modal__image-container {
          width: 100%;
          height: 100%;

          display: flex;
          align-items: center;
          justify-content: center;

          overflow: hidden;

          touch-action: none;
          overscroll-behavior: contain;
        }

        .image-carousel-modal__image {
          width: 100%;
          height: 100%;

          display: block;

          object-fit: contain;

          user-select: none;
          -webkit-user-drag: none;

          transform-origin: center center;

          will-change: transform;

          touch-action: none;
        }

        .image-carousel-modal__header {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;

          z-index: 30;

          display: flex;
          align-items: center;
          justify-content: space-between;

          padding: 18px 20px;

          pointer-events: none;

          background: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.55),
            transparent
          );
        }

        .image-carousel-modal__counter {
          padding: 6px 10px;

          border-radius: 8px;

          background: rgba(255, 255, 255, 0.1);

          color: #fff;

          font-size: 13px;
          font-weight: 500;

          backdrop-filter: blur(8px);
        }

        .image-carousel-modal__close {
          width: 40px;
          height: 40px;

          display: grid;
          place-items: center;

          padding: 0;

          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 50%;

          background: rgba(255, 255, 255, 0.1);

          color: #fff;

          cursor: pointer;

          backdrop-filter: blur(10px);

          pointer-events: auto;

          transition:
            background 150ms ease,
            transform 150ms ease;
        }

        .image-carousel-modal__close:hover {
          background: rgba(255, 255, 255, 0.18);

          transform: scale(1.05);
        }

        .image-carousel-modal__close:active {
          transform: scale(0.95);
        }

        .image-carousel-modal__close svg {
          width: 18px;
          height: 18px;
        }

        .image-carousel-modal__arrow {
          position: absolute;
          top: 50%;

          z-index: 20;

          width: 46px;
          height: 46px;

          display: grid;
          place-items: center;

          padding: 0;

          transform: translateY(-50%);

          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 50%;

          background: rgba(255, 255, 255, 0.1);

          color: #fff;

          cursor: pointer;

          backdrop-filter: blur(10px);

          transition:
            background 150ms ease,
            transform 150ms ease;
        }

        .image-carousel-modal__arrow:hover {
          background: rgba(255, 255, 255, 0.18);

          transform:
            translateY(-50%)
            scale(1.05);
        }

        .image-carousel-modal__arrow--left {
          left: 20px;
        }

        .image-carousel-modal__arrow--right {
          right: 20px;
        }

        .image-carousel-modal__arrow svg {
          width: 22px;
          height: 22px;
        }

        /*
         * Zoom vertical en bas à gauche
         */
        .image-carousel-modal__zoom {
          position: absolute;

          left: 20px;
          bottom: 20px;

          z-index: 30;

          width: 38px;
          height: 150px;

          display: flex;
          align-items: center;
          justify-content: center;

          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 12px;

          background: rgba(0, 0, 0, 0.55);

          backdrop-filter: blur(12px);
          margin-botton: 5px;

          box-shadow:
            0 4px 20px rgba(0, 0, 0, 0.3);
        }

        .image-carousel-modal__zoom
          input[type="range"] {
          position: absolute;

          width: 110px;
          height: 4px;

          margin: 0;

          appearance: none;
          -webkit-appearance: none;

          border: 0;
          border-radius: 999px;

          background: rgba(255, 255, 255, 0.35);

          cursor: pointer;

          transform: rotate(-90deg);
        }

        .image-carousel-modal__zoom
          input[type="range"]::-webkit-slider-thumb {
          width: 14px;
          height: 14px;

          appearance: none;
          -webkit-appearance: none;

          border: 0;
          border-radius: 50%;

          background: #4a90c2;

          box-shadow:
            0 0 0 5px rgba(74, 144, 194, 0.12);

          cursor: grab;
        }

        .image-carousel-modal__zoom
          input[type="range"]::-moz-range-thumb {
          width: 14px;
          height: 14px;

          border: 0;
          border-radius: 50%;

          background: #4a90c2;

          box-shadow:
            0 0 0 5px rgba(74, 144, 194, 0.12);

          cursor: grab;
        }

        .image-carousel-modal__zoom-value {
          position: absolute;

          bottom: -24px;

          color: #ccc;

          font-size: 10px;
          font-weight: 500;

          user-select: none;
          white-space: nowrap;
        }

        /*
         * Miniatures
         */
        .image-carousel-modal__thumbnails {
          position: absolute;

          left: 50%;
          bottom: 18px;

          z-index: 30;

          display: flex;
          gap: 8px;

          max-width: calc(100% - 150px);

          padding: 8px;

          transform: translateX(-50%);

          overflow-x: auto;

          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 12px;

          background: rgba(0, 0, 0, 0.55);

          box-shadow:
            0 4px 20px rgba(0, 0, 0, 0.3);

          backdrop-filter: blur(12px);

          scrollbar-width: none;
        }

        .image-carousel-modal__thumbnails::-webkit-scrollbar {
          display: none;
        }

        .image-carousel-modal__thumbnail {
          flex: 0 0 68px;

          width: 68px;
          height: 48px;

          padding: 0;

          overflow: hidden;

          border: 2px solid transparent;
          border-radius: 6px;

          background: transparent;

          opacity: 0.55;

          cursor: pointer;

          transition:
            opacity 150ms ease,
            border-color 150ms ease,
            transform 150ms ease;
        }

        .image-carousel-modal__thumbnail:hover {
          opacity: 0.9;

          transform: translateY(-1px);
        }

        .image-carousel-modal__thumbnail--active {
          border-color: #4a90c2;
          opacity: 1;
        }

        .image-carousel-modal__thumbnail img {
          width: 100%;
          height: 100%;

          display: block;

          object-fit: cover;

          user-select: none;
          -webkit-user-drag: none;
        }

        .image-carousel-modal__close:focus-visible,
        .image-carousel-modal__arrow:focus-visible,
        .image-carousel-modal__thumbnail:focus-visible,
        .image-carousel-modal__zoom
          input:focus-visible {
          outline: 2px solid #4a90c2;
          outline-offset: 2px;
        }

        @media (max-width: 640px) {
          .image-carousel-backdrop {
            padding: 3vh 3vw;
          }

          .image-carousel-modal {
            width: 94vw;
            height: 94vh;

            border-radius: 14px;
          }

          .image-carousel-modal__header {
            padding: 12px;
          }

          .image-carousel-modal__close {
            width: 36px;
            height: 36px;
          }

          .image-carousel-modal__arrow {
            width: 40px;
            height: 40px;
          }

          .image-carousel-modal__arrow--left {
            left: 10px;
          }

          .image-carousel-modal__arrow--right {
            right: 10px;
          }

          .image-carousel-modal__zoom {
            left: 10px;
            bottom: 10px;

            width: 34px;
            height: 120px;
          }

          .image-carousel-modal__zoom
            input[type="range"] {
            width: 90px;
          }

          .image-carousel-modal__thumbnails {
            bottom: 10px;

            max-width: calc(100% - 90px);
          }

          .image-carousel-modal__thumbnail {
            flex-basis: 58px;

            width: 58px;
            height: 42px;
          }
        }
      `}</style>

      <div
        className='image-carousel-backdrop'
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            onClose()
          }
        }}
      >
        <div
          className='image-carousel-modal'
          role='dialog'
          aria-modal='true'
          aria-label="Galerie d'images"
          onWheel={handleWheel}
        >
          <header className='image-carousel-modal__header'>
            <span className='image-carousel-modal__counter'>
              {currentIndex + 1} / {images.length}
            </span>

            <button
              ref={closeButtonRef}
              type='button'
              className='image-carousel-modal__close'
              onClick={onClose}
              aria-label='Fermer la galerie'
            >
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                aria-hidden='true'
              >
                <path d='M6 6L18 18' />
                <path d='M18 6L6 18' />
              </svg>
            </button>
          </header>

          <main className='image-carousel-modal__content'>
            {images.length > 1 && (
              <button
                type='button'
                className='
                  image-carousel-modal__arrow
                  image-carousel-modal__arrow--left
                '
                onClick={previous}
                aria-label='Image précédente'
              >
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <path d='M15 18l-6-6 6-6' />
                </svg>
              </button>
            )}

            <div ref={imageContainerRef} className='image-carousel-modal__image-container'>
              {typeOfFile === 'application/pdf' ? (
                <iframe
                  key={currentImage}
                  src={currentImage}
                  title={`PDF ${currentIndex + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px',
                  }}
                />
              ) : (
                <img
                  key={currentImage}
                  src={currentImage}
                  alt={`Photo ${currentIndex + 1}`}
                  className='image-carousel-modal__image'
                  draggable={false}
                  style={{
                    transform: `
                    translate(
                      ${position.x}px,
                      ${position.y}px
                    )
                    scale(${zoom})
                  `,
                    cursor: zoom > MIN_ZOOM ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    transition: isDragging ? 'none' : 'transform 200ms ease',
                  }}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                />
              )}
            </div>

            {images.length > 1 && (
              <button
                type='button'
                className='
                  image-carousel-modal__arrow
                  image-carousel-modal__arrow--right
                '
                onClick={next}
                aria-label='Image suivante'
              >
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <path d='M9 18l6-6-6-6' />
                </svg>
              </button>
            )}
          </main>

          <div className='image-carousel-modal__zoom'>
            <input
              type='range'
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={ZOOM_STEP}
              value={zoom}
              onChange={handleZoomChange}
              aria-label='Niveau de zoom'
            />

            <span className='image-carousel-modal__zoom-value'>{Math.round(zoom * 100)}%</span>
          </div>

          {images.length > 1 && (
            <div
              className='image-carousel-modal__thumbnails'
              role='tablist'
              aria-label='Images de la galerie'
            >
              {images.map((image, index) => {
                const isActive = index === currentIndex
                const extension = image.split('.').pop()

                return (
                  <button
                    key={`${image}-${index}`}
                    type='button'
                    role='tab'
                    aria-selected={isActive}
                    aria-label={`Afficher la photo ${index + 1}`}
                    className={`
                      image-carousel-modal__thumbnail
                      ${isActive ? 'image-carousel-modal__thumbnail--active' : ''}
                    `}
                    onClick={() => changeImage(index)}
                  >
                    {extension === 'pdf' ? (
                      <div className='pdf-thumbnail d-flex flex-column'>
                        <span style={{fontSize: 30}}>📄</span>
                      </div>
                    ) : (
                      <img src={image} alt='' draggable={false} />
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
